<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { useAlert } from 'dashboard/composables';
import CustomAttribute from 'dashboard/components/CustomAttribute.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  conversationId: { type: [String, Number], required: true },
  contactId: { type: Number, default: null },
});

const { t } = useI18n();
const store = useStore();
const isReprocessing = ref(false);
const showPendingOnly = ref(false);

const currentChat = computed(
  () => store.getters.getConversationById(props.conversationId) || {}
);

const inbox = computed(() => {
  const inboxId = currentChat.value?.inbox_id;
  return inboxId ? store.getters['inboxes/getInbox'](inboxId) : {};
});

const prefillConfig = computed(() => inbox.value?.ai_prefill_config || {});

const activeForm = computed(() => {
  const forms = prefillConfig.value.forms || [];
  const activeId = prefillConfig.value.active_form_id;
  return forms.find(f => f.id === activeId) || forms[0];
});

const prefillMeta = computed(
  () => currentChat.value?.additional_attributes?.ai_prefill || {}
);

const suggestions = computed(() => prefillMeta.value.suggestions || []);

const customAttributes = computed(
  () => currentChat.value?.custom_attributes || {}
);

const conversationAttributeDefs = computed(() =>
  store.getters['attributes/getAttributesByModel']('conversation_attribute')
);

const formFields = computed(() => {
  if (!activeForm.value?.fields) return [];
  return activeForm.value.fields.map(field => {
    const def = conversationAttributeDefs.value.find(
      a => a.attribute_key === field.key
    );
    const suggestion = suggestions.value.find(s => s.key === field.key);
    const currentValue = customAttributes.value[field.key];
    return {
      key: field.key,
      label: field.label || def?.attribute_display_name || field.key,
      display_type: field.display_type || def?.attribute_display_type || 'text',
      description: field.description || def?.attribute_description || '',
      values: field.attribute_values || def?.attribute_values || [],
      regex_pattern: field.regex_pattern || def?.regex_pattern || null,
      regex_cue: field.regex_cue || def?.regex_cue || null,
      currentValue,
      suggestion,
      hasSuggestion: !!suggestion && !currentValue,
    };
  });
});

const visibleFields = computed(() =>
  showPendingOnly.value
    ? formFields.value.filter(f => !f.currentValue)
    : formFields.value
);

const hasFailed = computed(() => prefillMeta.value.status === 'failed');

async function onUpdateAttribute(key, value) {
  const updatedAttributes = { ...customAttributes.value, [key]: value };
  try {
    await store.dispatch('updateCustomAttributes', {
      conversationId: props.conversationId,
      customAttributes: updatedAttributes,
    });
    useAlert(t('CUSTOM_ATTRIBUTES.FORM.UPDATE.SUCCESS'));
  } catch {
    useAlert(t('CUSTOM_ATTRIBUTES.FORM.UPDATE.ERROR'));
  }
}

function onApplySuggestion(key) {
  const suggestion = suggestions.value.find(s => s.key === key);
  if (suggestion) {
    onUpdateAttribute(key, suggestion.value);
  }
}

async function onReprocess() {
  isReprocessing.value = true;
  try {
    await store.dispatch('reprocessAiPrefill', props.conversationId);
    useAlert(t('CONVERSATION_SIDEBAR.AI_PREFILL.REPROCESS_SUCCESS'));
  } catch {
    useAlert(t('CONVERSATION_SIDEBAR.AI_PREFILL.REPROCESS_ERROR'));
  } finally {
    isReprocessing.value = false;
  }
}
</script>

<template>
  <div class="px-2 pb-2">
    <!-- Error Banner -->
    <div
      v-if="hasFailed"
      class="mb-2 p-2 text-xs text-n-ruby-11 bg-n-ruby-2 rounded-lg"
    >
      {{
        `${$t('CONVERSATION_SIDEBAR.AI_PREFILL.ERROR')}: ${prefillMeta.error}`
      }}
    </div>

    <!-- Controls -->
    <div class="flex items-center justify-between mb-2 px-2">
      <div class="flex items-center gap-2">
        <button
          class="text-xs px-2 py-0.5 rounded-full"
          :class="showPendingOnly ? 'bg-n-alpha-2' : 'bg-n-alpha-1'"
          @click="showPendingOnly = !showPendingOnly"
        >
          {{
            showPendingOnly
              ? $t('CONVERSATION_SIDEBAR.AI_PREFILL.SHOW_ALL')
              : $t('CONVERSATION_SIDEBAR.AI_PREFILL.SHOW_PENDING')
          }}
        </button>
        <span class="text-xs text-n-slate-11">
          {{ `${visibleFields.length}/${formFields.length}` }}
        </span>
      </div>
      <NextButton
        xs
        faded
        icon="i-lucide-refresh-cw"
        :loading="isReprocessing"
        @click="onReprocess"
      >
        {{ $t('CONVERSATION_SIDEBAR.AI_PREFILL.REPROCESS') }}
      </NextButton>
    </div>

    <!-- No Fields -->
    <div v-if="!activeForm" class="text-sm text-n-slate-11 text-center p-4">
      {{ $t('CONVERSATION_SIDEBAR.AI_PREFILL.NO_FIELDS') }}
    </div>

    <!-- Fields -->
    <div v-else>
      <div
        v-if="visibleFields.length === 0"
        class="text-sm text-n-slate-11 text-center p-4"
      >
        {{ $t('CONVERSATION_SIDEBAR.AI_PREFILL.ALL_REVIEWED') }}
      </div>
      <div v-for="field in visibleFields" :key="field.key">
        <div class="relative">
          <!-- Suggestion Badge -->
          <div
            v-if="field.hasSuggestion"
            class="mx-4 mt-2 flex items-center gap-1.5"
          >
            <span
              class="text-xs font-medium text-n-teal-11 bg-n-teal-3 px-1.5 py-0.5 rounded"
            >
              {{ $t('CONVERSATION_SIDEBAR.AI_PREFILL.SUGGESTED') }}
            </span>
            <span
              v-if="field.suggestion.confidence"
              class="text-xs text-n-slate-11"
            >
              {{ `${Math.round(field.suggestion.confidence * 100)}%` }}
            </span>
            <span
              v-if="field.suggestion.ambiguous"
              class="text-xs text-n-amber-11"
            >
              {{
                field.suggestion.reason ||
                $t('CONVERSATION_SIDEBAR.AI_PREFILL.AMBIGUOUS')
              }}
            </span>
            <NextButton
              xs
              faded
              class="ml-auto"
              @click="onApplySuggestion(field.key)"
            >
              {{ $t('CONVERSATION_SIDEBAR.AI_PREFILL.APPLY') }}
            </NextButton>
          </div>
          <!-- Suggested value preview -->
          <div
            v-if="field.hasSuggestion"
            class="mx-4 mb-1 px-2 py-1.5 bg-n-teal-2 border border-n-teal-4 rounded text-sm text-n-slate-12 cursor-pointer hover:bg-n-teal-3"
            @click="onApplySuggestion(field.key)"
          >
            {{ field.suggestion.value }}
          </div>
          <CustomAttribute
            :attribute-key="field.key"
            :attribute-type="field.display_type"
            :label="field.label"
            :description="field.description"
            :values="field.values"
            :value="field.currentValue || (field.hasSuggestion ? '' : '')"
            :attribute-regex="field.regex_pattern"
            :regex-cue="field.regex_cue"
            :contact-id="contactId"
            show-actions
            @update="onUpdateAttribute"
            @delete="key => onUpdateAttribute(key, '')"
            @copy="val => navigator.clipboard.writeText(String(val))"
          />
        </div>
      </div>
    </div>
  </div>
</template>
